// src/calls/outgoingCallOutcome.js
//
// Phase N9D: pure extraction of OutgoingCallPage's status/endedReason ->
// message decision, so the distinct declined/no-answer/busy/cancelled/
// ended outcomes are directly unit-testable without hitting this
// project's known react-router-dom module-resolution limitation for
// component render tests.
//
// Verified server-side endedReason values this maps (see
// functions/calls/rejectCall.js, functions/calls/autoTimeoutCall.js,
// functions/calls/endCall.js): "rejected", "no_answer_timeout",
// "cancelled" / "cancelled_before_answer", "ended_by_user" (and any other
// reason falls back to the generic "ended" message - status itself is
// always "ended" for every terminal outcome, never "timeout"/"cancelled",
// so endedReason is the only signal that can distinguish them).

function resolveT(t) {
  return typeof t === "function"
    ? t
    : (_key, opts = {}) => {
        const defaultValue = opts?.defaultValue || "";
        if (!opts || typeof opts !== "object") return defaultValue;
        return defaultValue.replace(/\{\{(\w+)\}\}/g, (_, k) =>
          Object.prototype.hasOwnProperty.call(opts, k) ? String(opts[k]) : ""
        );
      };
}

/**
 * @param {{ status?: string, endedReason?: string|null, calleeName?: string }} call
 * @param {Function} [t] i18next translate function; falls back to raw
 *   English interpolation when omitted (keeps this callable from plain
 *   unit tests).
 * @return {string}
 */
export function resolveOutgoingCallMessage(call, t) {
  const tt = resolveT(t);
  const status = call?.status || "loading";
  const endedReason = call?.endedReason || null;
  const name = String(call?.calleeName || "").trim() || tt("user", { defaultValue: "User" });

  if (status === "ringing") {
    return tt("calls.outgoing.ringing", {
      defaultValue: "Waiting for the other person to answer...",
    });
  }
  if (status === "ongoing") {
    return tt("calls.outgoing.ongoing", { defaultValue: "Call started..." });
  }
  if (status === "ended") {
    if (endedReason === "rejected") {
      return tt("calls.outgoing.declined", {
        defaultValue: "{{name}} declined the call.",
        name,
      });
    }
    if (endedReason === "no_answer_timeout") {
      return tt("calls.outgoing.no_answer", {
        defaultValue: "{{name}} did not answer.",
        name,
      });
    }
    if (endedReason === "cancelled" || endedReason === "cancelled_before_answer") {
      return tt("calls.outgoing.cancelled", { defaultValue: "Call cancelled" });
    }
    return tt("calls.outgoing.ended", { defaultValue: "Call ended" });
  }
  if (status === "not_found") {
    return tt("calls.outgoing.not_found", { defaultValue: "Call not found" });
  }
  if (status === "invalid_id") {
    return tt("calls.outgoing.invalid_id", { defaultValue: "Invalid call ID" });
  }
  if (status === "error") {
    return tt("calls.outgoing.error", {
      defaultValue: "An error occurred during the call",
    });
  }

  return tt("calls.outgoing.connecting", { defaultValue: "Calling..." });
}
